/* eslint-disable max-lines */
import { createSelector } from 'reselect';
import { services, SupportedChainId, utils } from '@orionprotocol/sdk';
import { z } from 'zod';
import { invariant } from '@orionprotocol/common';

import { LumiaChainData, LumiaState } from '../slices';
import { RootStateType } from '../store';

const { isValidChainId } = utils;

const { pricesWithQuoteAssetSchema } = services.blockchainService.schemas;

export const MIGRATE_POOLS_DIRECT: Partial<
  Record<SupportedChainId, SupportedChainId>
> = {
  [SupportedChainId.ROPSTEN]: SupportedChainId.BSC_TESTNET,
  [SupportedChainId.MAINNET]: SupportedChainId.BSC,
}; // data format:  from => to

const flipObject = (obj: Partial<Record<string, string>>) =>
  Object.entries(obj).reduce<Partial<Record<string, string>>>(
    (prev, [key, value]) => ({
      ...prev,
      ...(value && {
        [value]: key,
      }),
    }),
    {},
  );

export const selectPoolIsAvailableInSeveralNetworks =
  (pool: string) =>
  ({ lumia }: RootStateType) => {
    const count = Object.keys(lumia)
      .filter(isValidChainId)
      .reduce((prev, curr) => {
        const currentOrion = lumia[curr];
        if (
          currentOrion &&
          currentOrion?.poolsInfo &&
          pool in currentOrion.poolsInfo.pools
        )
          return prev + 1;
        return prev;
      }, 0);
    return count > 1;
  };
export const getPoolsCanMigrate = ({ app, lumia }: RootStateType) => {
  const migrateChainId = MIGRATE_POOLS_DIRECT[app.chainId];
  if (!migrateChainId) return [];

  const poolsAvailableForMigration = Object.keys(
    lumia[migrateChainId]?.poolsInfo?.pools || {},
  );

  const currentChainPools = Object.keys(
    lumia[app.chainId]?.poolsInfo?.pools || {},
  );
  if (poolsAvailableForMigration.length && currentChainPools.length) {
    return poolsAvailableForMigration.filter((pool) =>
      currentChainPools.includes(pool),
    );
  }
  return [];
};
export const getPoolsConfig = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.poolsConfig;
export const getPoolConfig =
  (assetA: string, assetB: string) =>
  ({ app, lumia }: RootStateType) => {
    const pools = lumia[app.chainId]?.poolsConfig?.pools;
    return pools?.[`${assetA}-${assetB}`] || pools?.[`${assetB}-${assetA}`];
  };
export const getPoolsInfo = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.poolsInfo;
export const getExchangeContractAddress = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.exchangeContractAddress;
export const getSwapExecutorContractAddress = ({
  app,
  lumia,
}: RootStateType): string | undefined =>
  lumia[app.chainId]?.swapExecutorContractAddress;
export const getPriceOracleContractAddress = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.oracleContractAddress;
export const getMatcherAddress = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.matcherAddress;
export const getAssetToAddress = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.assetToAddress;
export const getInternalFeeAssets = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.internalFeeAssets;
export const getCexTokens = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.cexTokens;

export const getAddressToAsset = createSelector(
  ({ app, lumia }: RootStateType) => lumia[app.chainId]?.assetToAddress,
  (assetToAddress) => flipObject(assetToAddress ?? {}),
);

export const getPairsByNetwork =
  (network: SupportedChainId) =>
  ({ lumia }: RootStateType) =>
    lumia[network]?.pairs;

export const getAssets = createSelector(getAssetToAddress, (assetToAddress) =>
  Object.keys(assetToAddress ?? {}),
);

export type AggregatedAsset = {
  name: string;
  addresses: Partial<Record<SupportedChainId, string>>;
  available: SupportedChainId[];
};

export const getCurrentEnvNetworks = ({
  app: { env, envs },
}: RootStateType) => {
  if (!env) return [];
  const envInfo = envs[env];
  if (!envInfo) return [];
  return envInfo.networks;
};

const getOrionStateEqualityCheckerByFields =
  (fields: (keyof LumiaChainData)[]) => (a: LumiaState, b: LumiaState) => {
    const chainsA = Object.keys(a);
    const chainsB = Object.keys(b);

    if (chainsA.length !== chainsB.length) return false;

    const res = chainsA.reduce((prev, curr) => {
      if (prev === false) return prev;
      if (!utils.isValidChainId(curr)) return false;
      const orionA = a[curr];
      const orionB = b[curr];
      if (!orionA || !orionB) return false;
      return (
        prev &&
        fields.reduce(
          (prevState, currField) =>
            prevState && Object.is(orionA[currField], orionB[currField]),
          true,
        )
      );
    }, true);
    return res;
  };

export const getOrionWithEqualityCheckingByFields = (
  fields: (keyof LumiaChainData)[],
) =>
  createSelector(
    (state: RootStateType) => state.lumia,
    (result) => result,
    {
      memoizeOptions: {
        equalityCheck: getOrionStateEqualityCheckerByFields(fields),
      },
    },
  );

export const getOrionDataAggregatedByChain = createSelector(
  getCurrentEnvNetworks,
  ({ lumia }: RootStateType) => lumia,
  (networks, lumia) =>
    Object.keys(networks)
      .filter(isValidChainId)
      .map((chainId) => ({
        chainId,
        data: lumia[chainId],
      })),
);

export type AggregatedPairFromAggregator = {
  name: string;
  baseCurrency: string;
  quoteCurrency: string;
  available: SupportedChainId[];
};

export const getAggregatedPairsFromAggregator = createSelector(
  getCurrentEnvNetworks,
  ({ lumia }: RootStateType) => lumia,
  (networks, lumia) =>
    Object.keys(networks)
      .filter(isValidChainId)
      .reduce<Record<string, AggregatedPairFromAggregator>>((prev, network) => {
        const chainData = lumia[network];
        if (!chainData) return prev;

        const result = { ...prev };
        chainData?.pairs?.forEach((pairName) => {
          const [baseAsset, quoteAsset] = pairName.split('-');

          if (pairName in result) {
            result[pairName].available = [
              ...result[pairName].available,
              network,
            ];
          } else {
            result[pairName] = {
              name: pairName,
              baseCurrency: baseAsset,
              quoteCurrency: quoteAsset,
              available: [network],
            };
          }
        });
        return result;
      }, {}),
);

export const getAvailableNetworksPairsIsReady = createSelector(
  ({ lumia }: RootStateType) => lumia,
  getCurrentEnvNetworks,
  (lumia, networks) =>
    Object.keys(networks)
      .filter(isValidChainId)
      .reduce(
        (prev, chainId) =>
          prev && chainId in lumia && !!lumia?.[chainId]?.pairs?.length,
        true,
      ),
);

export const getCurrentNetworkPairsIsReady = createSelector(
  ({ lumia, app }: RootStateType) => ({ lumia, app }),
  getCurrentEnvNetworks,
  ({ lumia, app }, networks) => {
    const isChainValid =
      Object.keys(networks)
        .filter(isValidChainId)
        .find((chainId) => chainId === app.chainId) !== undefined;
    const orionByChain = lumia[app.chainId];

    return (
      isChainValid &&
      orionByChain !== undefined &&
      orionByChain?.pairs !== undefined &&
      orionByChain.pairs?.length !== undefined &&
      orionByChain.pairs.length > 0
    );
  },
);

export const getAggregatedTradableAssets = createSelector(
  getAggregatedPairsFromAggregator,
  (aggregatedPairs) => {
    const assets = new Map<string, SupportedChainId[]>();

    Object.values(aggregatedPairs).forEach(
      ({ baseCurrency, quoteCurrency, available }) => {
        available.forEach((currentChainId) => {
          const baseAssetRecord = assets.get(baseCurrency);

          if (!baseAssetRecord) assets.set(baseCurrency, [currentChainId]);
          else if (
            baseAssetRecord &&
            !baseAssetRecord.includes(currentChainId)
          ) {
            assets.set(baseCurrency, [...baseAssetRecord, currentChainId]);
          }

          const quoteAssetRecord = assets.get(quoteCurrency);
          if (!quoteAssetRecord) assets.set(quoteCurrency, [currentChainId]);
          else if (
            quoteAssetRecord &&
            !quoteAssetRecord.includes(currentChainId)
          ) {
            assets.set(quoteCurrency, [...quoteAssetRecord, currentChainId]);
          }
        });
      },
    );

    return Array.from(assets).reduce<Record<string, SupportedChainId[]>>(
      (prev, [assetName, chains]) => ({
        ...prev,
        [assetName]: chains,
      }),
      {},
    );
  },
);

export const getTradableAssets = createSelector(
  getAggregatedPairsFromAggregator,
  (aggregatedPairsFromAggregator) => {
    const assets = new Set<string>();
    Object.values(aggregatedPairsFromAggregator).forEach(
      ({ baseCurrency, quoteCurrency }) => {
        assets.add(baseCurrency);
        assets.add(quoteCurrency);
      },
    );
    return Array.from(assets);
  },
);

export const getGovernances = createSelector(
  getCurrentEnvNetworks,
  getOrionWithEqualityCheckingByFields(['poolsConfig']),
  (networks, lumia) =>
    Object.keys(networks)
      .filter(isValidChainId)
      .map((chainId) => ({
        chainId,
        governanceAddress: lumia[chainId]?.poolsConfig?.governanceAddress,
      })),
);

export const getPrices = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.prices;
export const getPricesWithQuoteAsset = ({
  app,
  lumia,
}: RootStateType): z.infer<typeof pricesWithQuoteAssetSchema> | undefined =>
  lumia[app.chainId]?.pricesWithQuoteAsset;
export const getGasPrice = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.gasPrice;
export const getAssetToIcons = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.assetToIcons;
export const getAssetToDecimals = ({ app, lumia }: RootStateType) =>
  lumia[app.chainId]?.assetToDecimals;
export const getAtomicSwapAssets =
  (chain: SupportedChainId | undefined) =>
  ({ app, lumia }: RootStateType) => {
    const { env, envs } = app;
    if (!env || !envs) return [];
    const envInfo = envs[env];
    if (!envInfo) return [];
    if (chain === undefined) return [];
    invariant(isValidChainId(chain));
    return lumia[chain]?.atomicSwapAssets ?? [];
  };

export const getAggregatedAtomicSwapAssets = ({
  app,
  lumia,
}: RootStateType) => {
  const { env, envs } = app;
  if (!env || !envs) return [];
  const envInfo = envs[env];
  if (!envInfo) return [];
  const chains = Object.keys(lumia);
  const assetsSet = chains
    .filter(isValidChainId)
    .reduce<Set<string>>((prev, currentChain) => {
      const chain = lumia[currentChain];
      if (!chain) return prev;
      const assets = chain.atomicSwapAssets;
      if (assets) assets.forEach((asset) => prev.add(asset));
      return prev;
    }, new Set<string>());
  return Array.from(assetsSet);
};

export const getTokenAddressByAssetName =
  (assetName: string | undefined) =>
  ({ app, lumia }: RootStateType) =>
    lumia[app.chainId]?.assetToAddress?.[assetName ?? ''];

type InternalFeeAsset = {
  value: number;
  type: 'plain' | 'percent';
  asset: string;
};

export const getInternalFeeAssetsMap = createSelector(
  getInternalFeeAssets,
  (feeTokens) =>
    feeTokens?.reduce<Map<string, InternalFeeAsset>>((feeTokensMap, info) => {
      feeTokensMap.set(info.asset, info);
      return feeTokensMap;
    }, new Map()),
);

export const getCexAssetsSet = createSelector(
  getCexTokens,
  (cexTokens) => new Set(cexTokens),
);
